/**
 * Formatting, mono and time (mission 6 T014; FR-024, GL.5; research §1.12;
 * style guide §3, §6).
 *
 * - Counts use en-US grouping. Poppins has no tabular figures, so any count in
 *   a table, a credit, a hash, a filename, an id or a mask renders inside
 *   `<Mono>` (CLAUDE.md §5).
 * - Dates and times are UTC; a view that shows one states "Times in UTC" once
 *   (register 17) and puts the machine form in `<time dateTime>`.
 * - Bytes are binary with one decimal, a trailing `.0` dropped; the exact byte
 *   count goes in the `aria-label` beside them.
 * - A run's short label is the first 8 characters of its id (C-010).
 *
 * Written with `createElement` so the module stays `.ts`. Frozen after mission
 * 6 WP02 (T17).
 *
 * Mission 9 WP11 T066 (U25, C-010): `money(cents)` renders every price the API
 * sends in cents (`$100.00`); with `per` it is a plan price (`$100/month`),
 * a whole-dollar `.00` dropped. No price is ever a literal in a sentence.
 */
import { createElement, type HTMLAttributes, type ReactElement } from 'react'

import { cn } from '@/components/ui/cn'

const COUNT = new Intl.NumberFormat('en-US')
const DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
const TIME = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'UTC' })

export function count(n: number): string {
  return COUNT.format(n)
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

/** US dollars from integer cents; `per` gives a plan price per interval (U25). */
export function money(cents: number, options: { per?: 'month' | 'year' } = {}): string {
  if (!Number.isInteger(cents)) throw new RangeError('money: cents must be a whole number')
  const text = USD.format(cents / 100)
  return options.per === undefined ? text : `${text.replace(/\.00$/, '')}/${options.per}`
}

export function date(iso: string): string {
  return DATE.format(new Date(iso))
}

/** 24-hour UTC, `HH:mm`. */
export function time(iso: string): string {
  return TIME.format(new Date(iso))
}

/** The machine-readable form for `<time dateTime>`. */
export function isoDateTime(iso: string): string {
  return new Date(iso).toISOString()
}

const UNITS = ['B', 'KB', 'MB', 'GB'] as const

export function bytes(n: number): string {
  let value = n
  let unit = 0
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit++
  }
  const text = unit === 0 ? String(value) : value.toFixed(1).replace(/\.0$/, '')
  return `${text} ${UNITS[unit]}`
}

/** The exact byte count, for an `aria-label` beside `bytes()`. */
export function bytesExact(n: number): string {
  return `${count(n)} ${n === 1 ? 'byte' : 'bytes'}`
}

export function shortId(id: string): string {
  return id.slice(0, 8)
}

export function sha256(hex: string): string {
  return hex.toLowerCase()
}

/** Mono for ids, counts, credits, hashes, filenames and masks (style guide §3). */
export function Mono({ className, ...rest }: HTMLAttributes<HTMLSpanElement>): ReactElement {
  return createElement('span', { ...rest, className: cn('font-mono', className) })
}
